import React, { useState, useCallback, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Stack,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import DownloadIcon from '@mui/icons-material/Download';
import DeleteIcon from '@mui/icons-material/Delete';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import { useTranslation } from 'react-i18next';
import Table from '../../../../common/components/Table/Table';
import { NotificationSnackbar } from '../../../../common/components/Snackbar';
import axios from 'axios';

interface ASIModalProps {
  isOpen: boolean;
  onClose: () => void;
  row: any;
  dataSetId: number;
  fileDetails?: any;
}

interface AttachmentFile {
  id: string;
  inboundPpo: string;
  outboundPpo: string;
  sampleId: string;
  uploadDate: string;
  uploadedBy: string;
  removalDate: string | null;
  filename: string;
}

const ASIModal: React.FC<ASIModalProps> = ({ isOpen, onClose, row, dataSetId, fileDetails }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // State management
  const [attachments, setAttachments] = useState<AttachmentFile[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    type: 'info',
  });

  // Helper function to extract query parameters from row and fileDetails
  const getQueryParams = React.useCallback((): URLSearchParams => {
    const params = new URLSearchParams();
    
    // Use year and quarter directly from fileDetails (preferred)
    // Fall back to calculating from dates if fileDetails doesn't have them
    let year: number;
    let quarter: number;
    
    if (fileDetails?.accountingYear) {
      year = fileDetails.accountingYear;
    } else {
      // Try to extract from row dates
      let date: Date | null = null;
      
      if (row?.despatchDate) {
        date = new Date(row.despatchDate);
        if (isNaN(date.getTime())) date = null;
      }
      
      // Fallback to current date if all else fails
      if (!date) {
        date = new Date();
      }
      
      year = date.getFullYear();
    }
    
    if (fileDetails?.accountingQuarter) {
      quarter = fileDetails.accountingQuarter;
    } else {
      // Try to extract from row dates
      let date: Date | null = null;
      
      if (row?.despatchDate) {
        date = new Date(row.despatchDate);
        if (isNaN(date.getTime())) date = null;
      }
      
      // Fallback to current date if all else fails
      if (!date) {
        date = new Date();
      }
      
      quarter = Math.floor(date.getMonth() / 3) + 1;
    }
    
    // Set year and quarter (validate to ensure they're not NaN)
    if (isNaN(year) || year < 1900 || year > 2100) {
      console.warn('Invalid year extracted:', year);
      year = new Date().getFullYear();
    }
    
    if (isNaN(quarter) || quarter < 1 || quarter > 4) {
      console.warn('Invalid quarter extracted:', quarter);
      quarter = Math.floor(new Date().getMonth() / 3) + 1;
    }
    
    params.set('year', year.toString());
    params.set('quarter', quarter.toString());
    
    // Extract operator codes - use fileDetails as first priority
    const inboundOe = fileDetails?.operatorByInboundPpo || row?.receivingPpo || '';
    const outboundOe = fileDetails?.operatorByOutboundPpo || row?.sendingPpo || '';
    
    params.set('inboundOe', inboundOe);
    params.set('outboundOe', outboundOe);
    params.set('type', 'e55');
    
    return params;
  }, [row, fileDetails]);

  // Fetch attachments from API
  const fetchAttachments = React.useCallback(async () => {
    setIsLoading(true);
    try {
      // Get rowId from row data - use despatchSn/sampleId first
      const rowId = row?.despatchSn || row?.sampleId || row?.sampleDbId || row?.id;
      
      if (!rowId) {
        setNotification({
          open: true,
          message: t('asi.modal.noRowId', 'Despatch ID is required'),
          type: 'error',
        });
        setAttachments([]);
        setIsLoading(false);
        return;
      }

      // Get query parameters
      const queryParams = getQueryParams();
      
      // API endpoint: /attachments/list/{rowId}?type=e55&year=2024&quarter=1&inboundOe=XX&outboundOe=YY
      const response = await axios.get(`/attachments/list/${rowId}?${queryParams.toString()}`);
      
      setAttachments(response.data);
    } catch (error) {
      console.error('Error fetching attachments:', error);
      setNotification({
        open: true,
        message: t('asi.modal.fetchError', 'Failed to load attachments'),
        type: 'error',
      });
      // Set empty array as fallback
      setAttachments([]);
    } finally {
      setIsLoading(false);
    }
  }, [row, fileDetails, t, getQueryParams]);

  // Handle file selection
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    setSelectedFiles(files);
  };

  // Handle file upload
  const handleUpload = async () => {
    if (selectedFiles.length === 0) {
      setNotification({
        open: true,
        message: t('asi.modal.noFilesSelected', 'Please select files to upload'),
        type: 'warning',
      });
      return;
    }

    // Get rowId from row data - use despatchSn/sampleId first
    const rowId = row?.despatchSn || row?.sampleId || row?.sampleDbId || row?.id;
    
    if (!rowId) {
      setNotification({
        open: true,
        message: t('asi.modal.noRowId', 'Despatch ID is required for file upload'),
        type: 'error',
      });
      return;
    }

    setIsUploading(true);

    try {
      // Get query parameters
      const queryParams = getQueryParams();

      for (const file of selectedFiles) {
        const formData = new FormData();
        formData.append('file', file);

        // Upload endpoint: /attachments/upload/{rowId}?type=e55&year=2024&quarter=1&inboundOe=XX&outboundOe=YY
        const uploadUrl = `/attachments/upload/${rowId}?${queryParams.toString()}`;

        const response = await axios.post(uploadUrl, formData, {
          // Axios automatically sets Content-Type to multipart/form-data with boundary
          // No need to set it explicitly, as axios handles it correctly
        });

        // Response is automatically parsed by axios
        const result = response.data;
      }

      // Clear selected files after successful upload
      setSelectedFiles([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Refresh attachments list
      await fetchAttachments();

      setNotification({
        open: true,
        message: t('asi.modal.uploadSuccess', 'Files uploaded successfully'),
        type: 'success',
      });
    } catch (error) {
      console.error('Error uploading files:', error);
      setNotification({
        open: true,
        message: t('asi.modal.uploadError', 'Failed to upload files'),
        type: 'error',
      });
    } finally {
      setIsUploading(false);
    }
  };

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  // Handle file download
  const handleDownload = async (attachment: AttachmentFile) => {
    try {
      // Download endpoint: /attachments/download/{attachmentId}
      const downloadUrl = `/attachments/download/${attachment.id}`;
      
      const response = await axios.get(downloadUrl, {
        responseType: 'blob', // Important for file downloads
      });

      // Extract filename from Content-Disposition header
      let fileName: string = attachment.filename || 'download';
      
      const contentDisposition = 
        response.headers['content-disposition'] || 
        response.headers['Content-Disposition'];
      
      if (contentDisposition) {
        // Match the filename in Content-Disposition header
        const filenameMatch = /filename[^;=\n]*=(['"]?)([^"';]+)\1/i.exec(contentDisposition);
        if (filenameMatch && filenameMatch[2]) {
          fileName = decodeURIComponent(filenameMatch[2]);
        }
      }

      // Create download link
      const blob = response.data;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setNotification({
        open: true,
        message: t('asi.modal.downloadSuccess', 'File downloaded successfully'),
        type: 'success',
      });
    } catch (error) {
      console.error('Error downloading file:', error);
      setNotification({
        open: true,
        message: t('asi.modal.downloadError', 'Failed to download file'),
        type: 'error',
      });
    }
  };

  // Handle file deletion
  const handleDelete = async (attachment: AttachmentFile) => {
    try {
      // Delete endpoint would be: /attachments/delete/{attachmentId}
      await axios.delete(`/attachments/delete/${attachment.id}`);

      // Refresh attachments list
      await fetchAttachments();

      setNotification({
        open: true,
        message: t('asi.modal.deleteSuccess', 'File deleted successfully'),
        type: 'success',
      });
    } catch (error) {
      console.error('Error deleting file:', error);
      setNotification({
        open: true,
        message: t('asi.modal.deleteError', 'Failed to delete file'),
        type: 'error',
      });
    }
  };

  // Define table columns
  const columns = useMemo(
    () => [
      {
        Header: t('asi.modal.table.id', 'ID'),
        accessor: 'id',
        width: '10%',
      },
      {
        Header: t('asi.modal.table.inboundPpo', 'Inbound PPO'),
        accessor: 'exchangeOfficeByInboundOe',
        width: '10%',
      },
      {
        Header: t('asi.modal.table.outboundPpo', 'Outbound PPO'),
        accessor: 'exchangeOfficeByOutboundOe',
        width: '10%',
      },
      {
        Header: t('asi.modal.table.uploadDate', 'Upload Date'),
        accessor: 'uploadDate',
        width: '15%',
        Cell: ({ value }: { value: string }) => formatDate(value),
      },
      {
        Header: t('asi.modal.table.uploadedBy', 'Uploaded By'),
        accessor: 'uploader',
        width: '10%',
      },
      {
        Header: t('asi.modal.table.removalDate', 'Removal Date'),
        accessor: 'removalDate',
        width: '15%',
        Cell: ({ value }: { value: string | null }) => formatDate(value),
      },
      {
        Header: t('asi.modal.table.filename', 'Filename'),
        accessor: 'fileName',
        width: '20%',
      },
      {
        Header: t('asi.modal.table.actions', 'Actions'),
        accessor: 'id', // Use 'id' as accessor since we need the row data
        width: '8%',
        Cell: ({ value }: { value: string }) => {
          // Find the attachment by id to get full row data
          const attachment = attachments.find(att => att.id === value);
          if (!attachment) return null;
          
          return (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => handleDownload(attachment)}
                title={t('asi.modal.download', 'Download')}
              >
                <DownloadIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDelete(attachment)}
                title={t('asi.modal.delete', 'Delete')}
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          );
        },
      },
    ],
    [t, attachments]
  );

  // Handle context menu actions for table
  const handleContextMenuAction = (action: string, attachment: AttachmentFile) => {
    switch (action) {
      case 'download':
        handleDownload(attachment);
        break;
      case 'delete':
        handleDelete(attachment);
        break;
      default:
        break;
    }
  };

  // Fetch attachments when modal opens
  React.useEffect(() => {
    if (isOpen && row) {
      fetchAttachments();
    }
  }, [isOpen, row, fetchAttachments]);

  // Handle notification close
  const handleNotificationClose = () => {
    setNotification(prev => ({ ...prev, open: false }));
  };

  // Handle choose file button click
  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  return (
    <>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { minHeight: '600px' }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {t('asi.modal.title', 'ADI (Additional Despatch Information)')}
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Stack spacing={3}>
            {/* File Upload Section */}
            <Paper elevation={1} sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                {t('asi.modal.uploadSection.title', 'Upload Files')}
              </Typography>
              
              <Box sx={{ mb: 3 }}>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  multiple
                  style={{ display: 'none' }}
                  accept="*/*"
                />
                
                <Stack direction="row" spacing={2} alignItems="center">
                  <Button
                    variant="outlined"
                    startIcon={<FolderOpenIcon />}
                    onClick={handleChooseFile}
                    disabled={isUploading}
                  >
                    {t('asi.modal.chooseFiles', 'Choose Files')}
                  </Button>
                  
                  <Button
                    variant="contained"
                    startIcon={isUploading ? <CircularProgress size={20} color="inherit" /> : <UploadFileIcon />}
                    onClick={handleUpload}
                    disabled={selectedFiles.length === 0 || isUploading}
                  >
                    {isUploading 
                      ? t('asi.modal.uploading', 'Uploading...') 
                      : t('asi.modal.upload', 'Upload')
                    }
                  </Button>
                </Stack>

                {selectedFiles.length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('asi.modal.selectedFiles', 'Selected files:')}
                    </Typography>
                    {selectedFiles.map((file, index) => (
                      <Typography key={index} variant="body2">
                        • {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </Typography>
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>

            <Divider />

            {/* Attachments Table */}
            <Box>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Table
                  data={attachments}
                  columns={columns}
                  moduleName={t('asi.modal.attachments.moduleName', 'Despatch Attachments')}
                  viewMode={true}
                  hideDefaultActions={true}
                  isRowClickable={false}
                  pageSize={10}
                  page={0}
                  totalCount={attachments.length}
                  onPageChange={() => {}}
                  onPageSizeChange={() => {}}
                />
              )}
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} variant="outlined">
            {t('asi.modal.close', 'Close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.type}
        onClose={handleNotificationClose}
        data-testid="asi-modal-notification"
      />
    </>
  );
};

export default ASIModal; 